import React from 'react'
import { useToast } from '../context/ToastContext'
import { friendsApi } from '../api/friends'
import { COUNTRIES } from '../data/Countries'

export default function GlobalLookup() {
    const [firstName, setFirstName] = React.useState('')
    const [lastName, setLastName]   = React.useState('')
    const [city, setCity]           = React.useState('')
    const [country, setCountry]     = React.useState('')
    const [results, setResults]     = React.useState(null)
    const [isFriendsView, setIsFriendsView] = React.useState(false)
    const [error, setError]         = React.useState('')
    const [connecting, setConnecting] = React.useState(null)
    const { addToast } = useToast()

    async function handleSearch(e) {
        e.preventDefault()
        if (!firstName.trim() && !lastName.trim() && !city.trim() && !country) {
            setError('Enter a value in one of the filters')
            return
        }
        setError('')
        setIsFriendsView(false)
        try {
            const data = await friendsApi.search({ firstName, lastName, city, country })
            setResults(data)
        } catch (err) {
            addToast(err.message, 'error')
        }
    }

    async function handleMyFriends() {
        setError('')
        setIsFriendsView(true)
        try {
            const data = await friendsApi.listFriends()
            setResults(data.map(f => ({
                id: f.friend_id,
                first_name: f.first_name,
                last_name: f.last_name,
                city: f.city,
                country: f.country,
            })))
        } catch (err) {
            addToast(err.message, 'error')
        }
    }

    async function handleConnect(user) {
        setConnecting(user.id)
        try {
            await friendsApi.addFriend(user.id)
            addToast(`Connected with ${user.first_name} ${user.last_name}`, 'success')
            setResults(prev => prev.filter(u => u.id !== user.id))
        } catch (err) {
            addToast(err.message, 'error')
        } finally {
            setConnecting(null)
        }
    }

    const inputClass = "w-full px-3 py-2 rounded-md border border-gray-300 font-karla text-sm text-bankroll-text focus:outline-none focus:ring-2 focus:ring-bankroll-primary"
    const labelClass = "block text-xs font-bold text-gray-300 uppercase tracking-wide mb-1"

    return (
        <main className="page-main">
            <form onSubmit={handleSearch} className="bg-white rounded-[10px] px-5 py-5 shadow-sm flex flex-col gap-4">
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                    <div>
                        <label className={labelClass}>First Name</label>
                        <input className={inputClass} value={firstName} onChange={e => setFirstName(e.target.value)} />
                    </div>
                    <div>
                        <label className={labelClass}>Last Name</label>
                        <input className={inputClass} value={lastName} onChange={e => setLastName(e.target.value)} />
                    </div>
                    <div>
                        <label className={labelClass}>City</label>
                        <input className={inputClass} value={city} onChange={e => setCity(e.target.value)} />
                    </div>
                    <div>
                        <label className={labelClass}>Country</label>
                        <select className={inputClass} value={country} onChange={e => setCountry(e.target.value)}>
                            <option value="">All Countries</option>
                            {COUNTRIES.map(c => (
                                <option key={c} value={c}>{c}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {error && (
                    <p className="text-sm text-bankroll-danger m-0">{error}</p>
                )}

                <div className="flex gap-3">
                    <button
                        type="submit"
                        className="px-6 py-2 border-0 rounded-md bg-bankroll-primary text-white font-karla text-sm font-bold cursor-pointer hover:opacity-85"
                    >
                        Search
                    </button>
                    <button
                        type="button"
                        onClick={handleMyFriends}
                        className="px-6 py-2 border-0 rounded-md bg-bankroll-teal text-bankroll-on-teal font-karla text-sm font-bold cursor-pointer hover:opacity-85"
                    >
                        My Friends
                    </button>
                </div>
            </form>

            {results !== null && (
                results.length === 0 ? (
                    <p className="text-gray-400 text-[0.95rem]">No users found.</p>
                ) : (
                    <>
                        {/* Mobile: card list */}
                        <ul className="sm:hidden list-none m-0 p-0 flex flex-col gap-3">
                            {results.map(user => (
                                <li key={user.id} className="bg-white rounded-[10px] shadow-sm px-4 py-3 flex items-center justify-between gap-3">
                                    <div className="flex flex-col gap-0.5 min-w-0">
                                        <span className="font-bold text-bankroll-text text-sm truncate">
                                            {user.first_name} {user.last_name}
                                        </span>
                                        <span className="text-xs text-gray-400 truncate">
                                            {[user.city, user.country].filter(Boolean).join(', ')}
                                        </span>
                                    </div>
                                    {isFriendsView ? (
                                        <span className="shrink-0 px-4 py-1.5 rounded-md bg-gray-100 text-gray-500 font-karla text-sm font-bold">
                                            Friend
                                        </span>
                                    ) : (
                                        <button
                                            onClick={() => handleConnect(user)}
                                            disabled={connecting === user.id}
                                            className="shrink-0 px-4 py-1.5 border-0 rounded-md bg-bankroll-primary text-white font-karla text-sm font-bold cursor-pointer hover:opacity-85 disabled:opacity-50"
                                        >
                                            {connecting === user.id ? 'Connecting…' : 'Connect'}
                                        </button>
                                    )}
                                </li>
                            ))}
                        </ul>

                        {/* Desktop: table */}
                        <div className="hidden sm:block bg-white rounded-[10px] shadow-sm overflow-x-auto">
                            <table className="border-collapse w-full" style={{ gridTemplateColumns: 'repeat(5, minmax(60px, 1fr))' }}>
                                <thead>
                                    <tr className="bg-bankroll-primary">
                                        <th className="text-left px-4 py-3 text-xs font-bold text-white uppercase tracking-wide whitespace-nowrap">First Name</th>
                                        <th className="text-left px-4 py-3 text-xs font-bold text-white uppercase tracking-wide whitespace-nowrap">Last Name</th>
                                        <th className="text-left px-4 py-3 text-xs font-bold text-white uppercase tracking-wide whitespace-nowrap">City</th>
                                        <th className="text-left px-4 py-3 text-xs font-bold text-white uppercase tracking-wide whitespace-nowrap">Country</th>
                                        <th className="text-left px-4 py-3 text-xs font-bold text-white uppercase tracking-wide whitespace-nowrap">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {results.map(user => (
                                        <tr key={user.id} className="border-b border-gray-100 last:border-0">
                                            <td className="px-4 py-3 text-sm text-bankroll-text whitespace-nowrap">{user.first_name}</td>
                                            <td className="px-4 py-3 text-sm text-bankroll-text whitespace-nowrap">{user.last_name}</td>
                                            <td className="px-4 py-3 text-sm text-bankroll-text whitespace-nowrap">{user.city}</td>
                                            <td className="px-4 py-3 text-sm text-bankroll-text whitespace-nowrap">{user.country}</td>
                                            <td className="px-4 py-3">
                                                {isFriendsView ? (
                                                    <span className="px-4 py-1.5 rounded-md bg-gray-100 text-gray-500 font-karla text-sm font-bold">
                                                        Friend
                                                    </span>
                                                ) : (
                                                    <button
                                                        onClick={() => handleConnect(user)}
                                                        disabled={connecting === user.id}
                                                        className="px-4 py-1.5 border-0 rounded-md bg-bankroll-primary text-white font-karla text-sm font-bold cursor-pointer hover:opacity-85 disabled:opacity-50"
                                                    >
                                                        {connecting === user.id ? 'Connecting…' : 'Connect'}
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )
            )}
        </main>
    )
}
